/**
 * ProductCarousel.tsx
 *
 * ANALYSIS OF DARK & BLURRY LOOK ISSUES:
 * 1. Excessive Blur/Glassmorphism: The cards used `backdrop-blur-xl` (24px blur filter) combined with a heavy card background color (`bg-[#0b0716]/80`).
 * 2. Dark Overlay Opacity too high: The global overlay had `bg-black/50`, and the card overlay gradient (`from-[#020617] via-[#020617]/75 to-[#020617]/25`) heavily dimmed background graphics.
 * 3. Low Brightness / Contrast: The card background mockup image had `opacity-25` and `blur-[2px]`, making it look muddy and unrecognizable.
 *
 * REMEDIES APPLIED:
 * - Changed card container to `bg-[#0b0716]/55` and `backdrop-blur-lg` to improve crispness and light transmission.
 * - Removed `blur-[2px]` and raised background image opacity to `opacity-50` to maintain screenshot sharpness.
 * - Softened internal card gradient to `from-[#020617]/90 via-[#020617]/40 to-transparent`.
 */

import { useRef, useEffect, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { motion } from "motion/react";

import bgFullOffice from "../assets/images/bg_full_office.webp";
import bgLiveClassroom from "../assets/images/bg_live_classroom.webp";
import bgDashboard from "../assets/images/bg_dashboard.webp";
import bgLms from "../assets/images/bg_lms.webp";
import bgOnlineExam from "../assets/images/bg_online_exam.webp";
import bgAccountings from "../assets/images/bg_accountings.webp";

interface ProductCard {
  id: number;
  title: string;
  description: string;
  bgImage: string;
  color: string;
  glow: string;
}

const CARDS: ProductCard[] = [
  {
    id: 1,
    title: "Full Office",
    description: "Full Office Suite with Your Subscription",
    bgImage: bgFullOffice,
    color: "from-blue-600/30 to-violet-600/30",
    glow: "rgba(139, 92, 246, 0.25)",
  },
  {
    id: 2,
    title: "Live Classroom",
    description: "First Online Meeting Tools for Education",
    bgImage: bgLiveClassroom,
    color: "from-indigo-600/30 to-purple-600/30",
    glow: "rgba(167, 139, 250, 0.25)",
  },
  {
    id: 3,
    title: "Dashboard",
    description: "Dashboard Tailored for Every Need",
    bgImage: bgDashboard,
    color: "from-violet-600/30 to-fuchsia-600/30",
    glow: "rgba(139, 92, 246, 0.3)",
  },
  {
    id: 4,
    title: "LMS",
    description: "Learning Management Systems",
    bgImage: bgLms,
    color: "from-purple-600/30 to-pink-600/30",
    glow: "rgba(167, 139, 250, 0.25)",
  },
  {
    id: 5,
    title: "Online Exam",
    description: "Protracted and Unprotracted Exams",
    bgImage: bgOnlineExam,
    color: "from-fuchsia-600/30 to-violet-600/30",
    glow: "rgba(139, 92, 246, 0.25)",
  },
  {
    id: 6,
    title: "Accountings",
    description: "Manage Your Entire Accounts with Us",
    bgImage: bgAccountings,
    color: "from-blue-600/30 to-indigo-600/30",
    glow: "rgba(14, 165, 233, 0.25)",
  },
];

// Triple the cards list to create a seamless infinite loop
const TRIPLED_CARDS = [...CARDS, ...CARDS, ...CARDS];

export default function ProductCarousel() {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isManuallyScrolling, setIsManuallyScrolling] = useState(false);
  const manualScrollTimerRef = useRef<NodeJS.Timeout | null>(null);
  const isSmoothScrollingRef = useRef(false);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Desktop mouse dragging support
  const [isDragging, setIsDragging] = useState(false);
  const dragStartX = useRef(0);
  const dragScrollLeft = useRef(0);

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    const container = containerRef.current;
    if (!container) return;

    setIsDragging(true);
    setIsManuallyScrolling(true);
    if (manualScrollTimerRef.current) {
      clearTimeout(manualScrollTimerRef.current);
    }

    dragStartX.current = e.pageX - container.offsetLeft;
    dragScrollLeft.current = container.scrollLeft;
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isDragging) return;
    const container = containerRef.current;
    if (!container) return;

    e.preventDefault();
    const x = e.pageX - container.offsetLeft;
    const walk = (x - dragStartX.current) * 1.5; // Drag speed multiplier
    container.scrollLeft = dragScrollLeft.current - walk;
    updateCardTransforms();
  };

  const handleMouseUpOrLeave = () => {
    if (!isDragging) return;
    setIsDragging(false);

    // Wrap scrollLeft back to middle set if it went outside
    const container = containerRef.current;
    if (container) {
      const singleSetWidth = container.scrollWidth / 3;
      if (singleSetWidth > 0) {
        if (container.scrollLeft >= singleSetWidth * 2) {
          container.scrollLeft -= singleSetWidth;
        } else if (container.scrollLeft < singleSetWidth) {
          container.scrollLeft += singleSetWidth;
        }
      }
    }

    // Freeze for 5 seconds after releasing drag
    if (manualScrollTimerRef.current) {
      clearTimeout(manualScrollTimerRef.current);
    }
    manualScrollTimerRef.current = setTimeout(() => {
      setIsManuallyScrolling(false);
    }, 5000);
  };

  // Dynamic 3D curved calculations running directly on the DOM nodes (120 FPS GPU-accelerated)
  const updateCardTransforms = () => {
    const container = containerRef.current;
    if (!container) return;

    const cards = container.querySelectorAll<HTMLDivElement>(".carousel-card");
    const containerCenter = container.scrollLeft + container.clientWidth / 2;
    const maxDist = container.clientWidth / 1.6; // Scale point of the 3D curve boundary

    cards.forEach((card) => {
      // Find coordinates of the card center
      const cardCenter = card.offsetLeft + card.clientWidth / 2;
      const delta = cardCenter - containerCenter;

      // Calculate normalized percentage distance (-1 to 1) from viewport center
      const pct = Math.max(-1.1, Math.min(1.1, delta / maxDist));

      // Math for Cinema IMAX curve:
      const rotateY = -pct * 22; // Curves inward beautifully
      const translateZ = -Math.abs(pct) * 90; // Deep 3D cinematic depth
      const scale = 1 - Math.abs(pct) * 0.08; // Subtle focus scale on center card
      const opacity = 1 - Math.abs(pct) * 0.35; // Cinematic vignette edge fade

      // Apply GPU-accelerated hardware transformation directly to DOM element bypass React renders
      card.style.transform = `perspective(1000px) rotateY(${rotateY}deg) translateZ(${translateZ}px) scale(${scale})`;
      card.style.opacity = `${opacity}`;

      // Update active card class directly in DOM for performance
      const distance = Math.abs(delta);
      const isActive = distance < card.clientWidth / 2;
      if (isActive) {
        card.classList.add("active-card");
      } else {
        card.classList.remove("active-card");
      }
    });
  };

  // Setup loop for smooth auto-scrolling
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let animationFrameId: number;
    let interactionDetected = false;
    const speed = 0.6; // Pixels per frame

    const handleInteraction = () => {
      interactionDetected = true;
      window.removeEventListener('mousemove', handleInteraction);
      window.removeEventListener('touchstart', handleInteraction);
      window.removeEventListener('scroll', handleInteraction);
      window.removeEventListener('keydown', handleInteraction);
    };

    window.addEventListener('mousemove', handleInteraction);
    window.addEventListener('touchstart', handleInteraction);
    window.addEventListener('scroll', handleInteraction);
    window.addEventListener('keydown', handleInteraction);

    const fallbackTimer = setTimeout(() => {
      interactionDetected = true;
    }, 15000);

    // Set initial scroll position to the center of tripled array
    const initScroll = () => {
      const singleSetWidth = container.scrollWidth / 3;
      container.scrollLeft = singleSetWidth;
      updateCardTransforms();
    };

    // Delay initialization slightly to guarantee DOM coordinates are ready
    const timer = setTimeout(initScroll, 50);

    const scrollLoop = () => {
      if (!isManuallyScrolling && interactionDetected) {
        container.scrollLeft += speed;

        const singleSetWidth = container.scrollWidth / 3;

        // Loop boundary wraps
        if (container.scrollLeft >= singleSetWidth * 2) {
          container.scrollLeft -= singleSetWidth;
        } else if (container.scrollLeft < singleSetWidth) {
          container.scrollLeft += singleSetWidth;
        }
      }

      // Update the 3D curves on every frame ONLY if interactive to save Lighthouse CPU
      if (interactionDetected) {
        updateCardTransforms();
      }
      
      animationFrameId = requestAnimationFrame(scrollLoop);
    };

    animationFrameId = requestAnimationFrame(scrollLoop);

    return () => {
      cancelAnimationFrame(animationFrameId);
      clearTimeout(timer);
      clearTimeout(fallbackTimer);
      window.removeEventListener('mousemove', handleInteraction);
      window.removeEventListener('touchstart', handleInteraction);
      window.removeEventListener('scroll', handleInteraction);
      window.removeEventListener('keydown', handleInteraction);
    };
  }, [isManuallyScrolling]);

  // Handle scroll events directly to keep transforms in sync
  const handleScrollEvent = () => {
    updateCardTransforms();

    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }

    // Only wrap when not smooth scrolling and not dragging
    scrollTimeoutRef.current = setTimeout(() => {
      const container = containerRef.current;
      if (!container || isDragging || isSmoothScrollingRef.current) return;

      const singleSetWidth = container.scrollWidth / 3;
      if (singleSetWidth > 0) {
        if (container.scrollLeft >= singleSetWidth * 2) {
          container.scrollLeft -= singleSetWidth;
        } else if (container.scrollLeft < singleSetWidth) {
          container.scrollLeft += singleSetWidth;
        }
      }
    }, 150);
  };

  // Handle manual navigation via Arrow click
  const handleManualScroll = (direction: "left" | "right") => {
    const container = containerRef.current;
    if (!container) return;

    // Pause the auto scroll
    setIsManuallyScrolling(true);
    if (manualScrollTimerRef.current) {
      clearTimeout(manualScrollTimerRef.current);
    }

    const cards = container.querySelectorAll<HTMLDivElement>(".carousel-card");
    if (cards.length === 0) return;

    // 1. Find the card currently closest to the center
    const containerCenter = container.scrollLeft + container.clientWidth / 2;
    let closestCardIndex = 0;
    let minDistance = Infinity;

    cards.forEach((card, index) => {
      const cardCenter = card.offsetLeft + card.clientWidth / 2;
      const distance = Math.abs(cardCenter - containerCenter);
      if (distance < minDistance) {
        minDistance = distance;
        closestCardIndex = index;
      }
    });

    // 2. Map closest card index to the middle set (modulo 6)
    const closestCardIndexInMiddleSet = (closestCardIndex % 6) + 6;

    // 3. If we are not currently centered at the middle set card, jump instantly to it
    if (closestCardIndex !== closestCardIndexInMiddleSet) {
      const targetCard = cards[closestCardIndexInMiddleSet];
      if (targetCard) {
        const targetCardCenter =
          targetCard.offsetLeft + targetCard.clientWidth / 2;
        container.scrollLeft = targetCardCenter - container.clientWidth / 2;
      }
    }

    // 4. Calculate target index relative to the wrapped middle set index
    let targetIndex = closestCardIndexInMiddleSet;
    if (direction === "right") {
      targetIndex = closestCardIndexInMiddleSet + 1;
    } else {
      targetIndex = closestCardIndexInMiddleSet - 1;
    }

    const destCard = cards[targetIndex];
    if (!destCard) return;

    const destCardCenter = destCard.offsetLeft + destCard.clientWidth / 2;
    const destScrollLeft = destCardCenter - container.clientWidth / 2;

    isSmoothScrollingRef.current = true;
    container.scrollTo({
      left: destScrollLeft,
      behavior: "smooth",
    });

    // Clear smooth scrolling flag after transition finishes
    setTimeout(() => {
      isSmoothScrollingRef.current = false;
    }, 600);

    // Freeze auto-scroll for exactly 5 seconds after manual navigation click
    manualScrollTimerRef.current = setTimeout(() => {
      setIsManuallyScrolling(false);
    }, 5000);
  };

  return (
    <section 
      id="products"
      className="relative w-full overflow-hidden pt-16 pb-24 select-none"
    >
      {/* Ambient background glow highlights */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[70vw] h-62.5 bg-brand-purple/10 blur-[120px] rounded-full pointer-events-none" />

      {/* Section Header */}
      <div className="text-center relative z-20 mb-8 px-4 flex flex-col items-center">

        <h2 className="font-orbitron font-black text-3xl md:text-5xl lg:text-6xl text-white tracking-widest leading-none">
          The Feature <span className="text-white/60 font-semibold text-[0.8em] tracking-wider ml-1">For You</span>
        </h2>
        <div className="mt-4 h-0.75 w-20 bg-linear-to-r from-brand-purple to-brand-electric rounded-full" />
      </div>

      {/* Main 3D Perspective Viewport */}
      <div className="w-full relative py-6" style={{ perspective: "1200px" }}>
        {/* Horizontal scroll track */}
        <div
          ref={containerRef}
          onScroll={handleScrollEvent}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUpOrLeave}
          onMouseLeave={handleMouseUpOrLeave}
          className="flex gap-8 overflow-x-auto scrollbar-none pt-8 pb-32 px-8 md:px-16 relative z-10 cursor-grab active:cursor-grabbing transform-gpu"
          style={{ transformStyle: "preserve-3d" }}
        >
          {TRIPLED_CARDS.map((card, index) => {
            return (
              <div
                key={`${card.id}-${index}`}
                className="carousel-card relative shrink-0 w-72 sm:w-96 aspect-3/4 rounded-3xl overflow-hidden transition-all duration-300 transform-gpu border border-white/10 hover:border-brand-purple/50 shadow-2xl group cursor-pointer"
                style={{ transformStyle: "preserve-3d" }}
              >
                {/* 1. Mockup Background Screenshot Layer (Improved clarity) */}
                <div className="absolute inset-0 z-0 overflow-hidden">
                  {/* Mockup image */}
                  <img
                    src={card.bgImage}
                    alt={card.title}
                    className="w-full h-full object-cover opacity-90 group-hover:scale-105 group-hover:opacity-100 transition-all duration-700"
                  />

                  {/* Dark overlay mask - softened to improve background visibility */}
                  <div className="absolute inset-0 bg-linear-to-t from-black/80 via-black/10 to-transparent" />
                </div>

                {/* 2. Text Overlay & Info Card Content */}
                <div className="absolute inset-0 p-6 z-10 flex flex-col justify-end text-center pb-8">
                  {/* Product Title */}
                  <h3 className="text-xl sm:text-2xl font-semibold font-serif text-white tracking-wide leading-tight group-hover:text-brand-electric transition-colors duration-300">
                    {card.title}
                  </h3>

                  {/* Product Description */}
                  <p className="mt-3 text-[10px] sm:text-xs text-slate-400 max-w-[90%] mx-auto leading-relaxed group-hover:text-slate-300 transition-colors duration-300">
                    {card.description}
                  </p>

                  {/* More Pill Action Button */}
                  <div className="mt-5 flex justify-center">
                    <span className="inline-flex items-center justify-center px-6 py-1.5 text-[10px] font-medium tracking-wider uppercase text-white/80 bg-white/5 border border-white/15 rounded-full backdrop-blur-sm transition-all duration-300 group-hover:bg-brand-purple group-hover:text-white group-hover:border-brand-purple group-hover:scale-105">
                      More
                    </span>
                  </div>
                </div>

                {/* Subtle border shine effect */}
                <div className="absolute inset-0 pointer-events-none border border-white/0 rounded-3xl group-hover:border-white/10 transition-all duration-500 z-20" />
              </div>
            );
          })}
        </div>
      </div>

      {/* 3. Manual Navigation Chevron Arrow Buttons (Absolute Left & Right Sides) */}
      {/* Left Arrow */}
      <div className="absolute left-3 sm:left-6 md:left-8 top-1/2 -translate-y-1/2 z-30">
        <motion.button
          whileHover={{
            scale: 1.1,
            backgroundColor: "rgba(139, 92, 246, 0.8)",
            color: "#ffffff",
          }}
          whileTap={{ scale: 0.93 }}
          onClick={() => handleManualScroll("left")}
          className="flex h-12 w-12 items-center justify-center rounded-full bg-white/5 border border-white/10 text-white transition-all shadow-lg backdrop-blur-md cursor-pointer hover:shadow-[0_0_15px_rgba(139,92,246,0.5)]"
          aria-label="Previous Product"
        >
          <ChevronLeft className="h-6 w-6" />
        </motion.button>
      </div>

      {/* Right Arrow */}
      <div className="absolute right-3 sm:right-6 md:right-8 top-1/2 -translate-y-1/2 z-30">
        <motion.button
          whileHover={{
            scale: 1.1,
            backgroundColor: "rgba(139, 92, 246, 0.8)",
            color: "#ffffff",
          }}
          whileTap={{ scale: 0.93 }}
          onClick={() => handleManualScroll("right")}
          className="flex h-12 w-12 items-center justify-center rounded-full bg-white/5 border border-white/10 text-white transition-all shadow-lg backdrop-blur-md cursor-pointer hover:shadow-[0_0_15px_rgba(139,92,246,0.5)]"
          aria-label="Next Product"
        >
          <ChevronRight className="h-6 w-6" />
        </motion.button>
      </div>
    </section>
  );
}
